import React, { useState } from "react";
import { FileText, CheckCircle2, Plus } from "lucide-react";
import { Panel, Empty } from "../components/ui";
import { formatDate } from "../utils/calendar.mjs";
import { needStatuses } from "../services/commercial.mjs";
function downloadQuote(q, client) {
  const text = `JM INTERIM — Devis V${q.version}\n${client?.name} — SIRET ${client?.siret}\nMétier : ${q.job}\nVolume : ${q.qty}\nCoefficient : ${q.coefficient ?? "—"}\nTaux : ${q.rate ?? "—"} €\nMontant estimé : ${q.amount ?? "—"} €\nConditions : ${q.conditions}\nValidité : ${formatDate(q.validUntil)}\nCommentaires : ${q.notes || ""}\nStatut : ${q.status}\nDocument de démonstration — prototype local`;
  const url = URL.createObjectURL(
    new Blob([text], { type: "text/plain;charset=utf-8" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = `devis-V${q.version}-${q.id.slice(0, 8)}.txt`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export default function Needs({
  state,
  focusId,
  onNew,
  onCreateQuote,
  onSend,
  onSign,
  onRefuse,
  onStatus,
  onPayload,
}) {
  const [filter, setFilter] = useState("all");
  const needs = state.needs.filter(
    (n) =>
      (!focusId || n.id === focusId) &&
      (filter === "all" || n.status === filter),
  );
  return (
    <>
      <Panel
        title="Besoins commerciaux"
        action={
          <button className="secondary" onClick={onNew}>
            <Plus />
            Nouveau besoin
          </button>
        }
      >
        <div className="taskFilters">
          <button
            aria-pressed={filter === "all"}
            className={filter === "all" ? "active" : ""}
            onClick={() => setFilter("all")}
          >
            Tous ({state.needs.length})
          </button>
          {needStatuses.map((status) => (
            <button
              key={status}
              aria-pressed={filter === status}
              className={filter === status ? "active" : ""}
              onClick={() => setFilter(status)}
            >
              {status} ({state.needs.filter((n) => n.status === status).length})
            </button>
          ))}
        </div>
        <p className="hint">
          Un seul besoin, plusieurs versions de devis possibles. La rubrique
          Devis de l’application existante pourra ouvrir ces versions.
        </p>
      </Panel>
      {needs.map((need) => {
        const client = state.clients.find((c) => c.id === need.clientId),
          contact =
            client?.contacts.find((c) => c.id === need.contactId) ||
            client?.contacts.find((c) => c.active && c.primary),
          quotes = state.quotes
            .filter((q) => q.needId === need.id)
            .sort((a, b) => b.version - a.version);
        return (
          <Panel key={need.id} title={client?.name || need.client}>
            <div className="needSummary">
              <div>
                <h3>
                  {need.job} × {need.qty}
                </h3>
                <span className="status">{need.status}</span>
                <p className="muted">
                  {need.type} · Démarrage : {formatDate(need.start)} · Contact :{" "}
                  {contact?.name || "À rechercher"}
                  <br />
                  Durée : {need.duration || "À compléter"} · Horaires :{" "}
                  {need.hours || "À compléter"}
                  <br />
                  Urgence : {need.urgency || "Non renseignée"} · Contraintes :{" "}
                  {need.constraints || "À compléter"}
                </p>
                {need.notes && <p className="muted">{need.notes}</p>}
              </div>
              {need.status !== "signé" && (
                <div className="needActions">
                  <button
                    className="primary"
                    onClick={() => onCreateQuote(need)}
                  >
                    <FileText />
                    {quotes.length
                      ? "Créer une nouvelle version"
                      : "Créer le devis"}
                  </button>
                  <select
                    aria-label={`Statut du besoin ${need.job}`}
                    value={need.status}
                    onChange={(e) => onStatus(need.id, e.target.value)}
                  >
                    {needStatuses.map((status) => (
                      <option
                        key={status}
                        disabled={["signé", "devis envoyé"].includes(status)}
                      >
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            {quotes.map((q) => (
              <div className="quoteVersion" key={q.id}>
                <div>
                  <b>Devis V{q.version}</b>{" "}
                  <span className="status">{q.status}</span>
                  <p className="muted">
                    Coefficient : {q.coefficient ?? "—"} · Taux :{" "}
                    {q.rate ?? "—"} € · Validité : {formatDate(q.validUntil)}
                    <br />
                    Conditions : {q.conditions}
                  </p>
                  {q.refusalReason && (
                    <p className="error">Refus : {q.refusalReason}</p>
                  )}
                </div>
                <div className="needActions">
                  <button
                    className="secondary"
                    onClick={() => downloadQuote(q, client)}
                  >
                    Ouvrir / télécharger le devis
                  </button>
                  {need.status !== "signé" &&
                    ["créé", "négociation"].includes(q.status) && (
                      <button className="primary" onClick={() => onSend(q.id)}>
                        Déclarer le devis envoyé
                      </button>
                    )}
                  {need.status !== "signé" &&
                    ["envoyé", "négociation"].includes(q.status) && (
                      <button className="primary" onClick={() => onSign(q.id)}>
                        <CheckCircle2 />
                        Enregistrer la signature
                      </button>
                    )}
                  {need.status !== "signé" && q.status !== "refusé" && (
                    <button className="secondary" onClick={() => onRefuse(q)}>
                      Devis refusé
                    </button>
                  )}
                </div>
              </div>
            ))}
            {need.transmittedAt && (
              <div className="success">
                <b>🟢 DEVIS SIGNÉ · Opportunité gagnée</b>
                <p>
                  ✓ Besoin transmis automatiquement au recrutement le{" "}
                  {new Date(need.transmittedAt).toLocaleString("fr-FR", { timeZone: "Europe/Paris", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  .
                </p>
                {need.missingFields?.length > 0 && (
                  <p>
                    Informations à compléter : {need.missingFields.join(", ")}
                  </p>
                )}
                <button
                  className="secondary"
                  onClick={() => onPayload(need.id)}
                >
                  Voir les informations transmises
                </button>
              </div>
            )}
          </Panel>
        );
      })}
      {!needs.length && <Empty>Aucun besoin dans cette étape.</Empty>}
      <p className="hint">
        Prototype front : les déclarations d’envoi et de signature sont
        enregistrées localement. Aucun email ni appel au système recrutement
        n’est envoyé.
      </p>
    </>
  );
}
